/**
 * SEO / GEO 统一配置工具
 * 提供站点默认 meta、结构化数据（JSON-LD）、页面 meta 生成等能力
 */

// ========== 站点基础信息 ==========
export const SITE_CONFIG = {
  name: "光华赋能",
  legalName: "杭州光华赋能教育科技有限公司",
  url: "https://www.myguanghua.com",
  logo: "https://www.myguanghua.com/img/logo.2d5c9335.png",
  ogImage: "https://www.myguanghua.com/img/logo.2d5c9335.png",
  description:
    "光华赋能始于2003年，专注企业科学经营培训与咨询陪跑，提供BLM科学经营、实战EMBA、企业内训、管理微咨询、数字化绩效系统等服务，用科学经营助推企业高质量发展。",
  keywords:
    "企业培训,管理咨询,科学经营,BLM,EMBA,企业内训,绩效管理,管理微咨询,数字化转型,光华赋能",
  foundingDate: "2003",
  address: {
    streetAddress: "文新街道文一西路522号8幢1单元301室",
    addressLocality: "杭州市西湖区",
    addressRegion: "浙江省",
    postalCode: "310012",
    addressCountry: "CN"
  },
  phone: "400-0808-155",
  email: "wangguoli@myguanghua.com",
  sameAs: [
    "https://weibo.com/u/7990026849",
    "https://weibo.com/1657054841",
    "https://weibo.com/2659704593",
    "https://weibo.com/1557846815"
  ]
};

// ========== Organization 结构化数据 ==========
export function getOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_CONFIG.name,
    legalName: SITE_CONFIG.legalName,
    url: SITE_CONFIG.url,
    logo: SITE_CONFIG.logo,
    foundingDate: SITE_CONFIG.foundingDate,
    description: SITE_CONFIG.description,
    telephone: SITE_CONFIG.phone,
    email: SITE_CONFIG.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE_CONFIG.address.streetAddress,
      addressLocality: SITE_CONFIG.address.addressLocality,
      addressRegion: SITE_CONFIG.address.addressRegion,
      postalCode: SITE_CONFIG.address.postalCode,
      addressCountry: SITE_CONFIG.address.addressCountry
    },
    sameAs: SITE_CONFIG.sameAs
  };
}

// ========== WebSite 结构化数据 ==========
export function getWebSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_CONFIG.name,
    url: SITE_CONFIG.url,
    description: SITE_CONFIG.description,
    inLanguage: "zh-CN"
  };
}

// ========== BreadcrumbList 结构化数据 ==========
export function getBreadcrumbJsonLd(items: Array<{ name: string; url?: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url ? `${SITE_CONFIG.url}${item.url}` : undefined
    }))
  };
}

// ========== Service 结构化数据 ==========
export function getServiceJsonLd(options: {
  name: string;
  description: string;
  category?: string;
  areaServed?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: options.category || "企业培训与咨询",
    name: options.name,
    description: options.description,
    provider: {
      "@type": "Organization",
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url
    },
    areaServed: options.areaServed || "中国"
  };
}

// ========== Article 结构化数据（资讯/案例详情页） ==========
export function getArticleJsonLd(options: {
  title: string;
  description?: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  articleBody?: string;
  url?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: options.title,
    description: options.description || "",
    image: options.image ? [options.image] : undefined,
    datePublished: options.datePublished || undefined,
    dateModified: options.dateModified || options.datePublished || undefined,
    author: {
      "@type": "Organization",
      name: options.authorName || SITE_CONFIG.name
    },
    publisher: {
      "@type": "Organization",
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
      logo: {
        "@type": "ImageObject",
        url: SITE_CONFIG.logo
      }
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": options.url || (typeof window !== "undefined" ? window.location.href : SITE_CONFIG.url)
    },
    articleBody: options.articleBody || undefined
  };
}

// ========== Course 结构化数据（课程详情页） ==========
export function getCourseJsonLd(options: {
  name: string;
  description?: string;
  provider?: string;
  instructor?: string;
  startDate?: string;
  endDate?: string;
  price?: string | number;
  currency?: string;
  url?: string;
  image?: string;
}) {
  const course: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: options.name,
    description: options.description || "",
    provider: {
      "@type": "Organization",
      name: options.provider || SITE_CONFIG.name,
      sameAs: SITE_CONFIG.url
    },
    url: options.url || (typeof window !== "undefined" ? window.location.href : SITE_CONFIG.url)
  };
  if (options.instructor) {
    course.instructor = {
      "@type": "Person",
      name: options.instructor
    };
  }
  if (options.startDate || options.endDate) {
    course.startDate = options.startDate || undefined;
    course.endDate = options.endDate || undefined;
  }
  if (options.price !== undefined && options.price !== null && options.price !== "") {
    course.offers = {
      "@type": "Offer",
      price: options.price,
      priceCurrency: options.currency || "CNY",
      availability: "https://schema.org/InStock"
    };
  }
  if (options.image) {
    course.image = options.image;
  }
  return course;
}

// ========== Person 结构化数据（师资详情页） ==========
export function getPersonJsonLd(options: {
  name: string;
  title?: string;
  description?: string;
  image?: string;
  worksFor?: string;
  url?: string;
  knowsAbout?: string | string[];
  gender?: string;
  address?: string;
  alumniOf?: string;
  sameAs?: string[];
  areaServed?: string;
  email?: string;
  telephone?: string;
}) {
  const person: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: options.name,
    jobTitle: options.title || undefined,
    description: options.description || undefined,
    image: options.image || undefined,
    worksFor: {
      "@type": "Organization",
      name: options.worksFor || SITE_CONFIG.name,
      url: SITE_CONFIG.url
    },
    url: options.url || (typeof window !== "undefined" ? window.location.href : SITE_CONFIG.url)
  };
  if (options.knowsAbout) {
    person.knowsAbout = Array.isArray(options.knowsAbout)
      ? options.knowsAbout
      : options.knowsAbout.split(/[、,，]/).filter(Boolean);
  }
  if (options.gender) {
    person.gender = options.gender;
  }
  if (options.address) {
    person.address = {
      "@type": "PostalAddress",
      addressLocality: options.address
    };
  }
  if (options.alumniOf) {
    person.alumniOf = {
      "@type": "CollegeOrUniversity",
      name: options.alumniOf
    };
  }
  if (options.sameAs && options.sameAs.length > 0) {
    person.sameAs = options.sameAs;
  }
  if (options.areaServed) {
    person.areaServed = options.areaServed;
  }
  if (options.email) {
    person.email = options.email;
  }
  if (options.telephone) {
    person.telephone = options.telephone;
  }
  return person;
}

// ========== 动态注入 JSON-LD（用于详情页异步数据） ==========
export function injectJsonLd(vmid: string, data: Record<string, any>) {
  if (typeof document === "undefined") return;
  // 移除旧的
  const old = document.getElementById(vmid);
  if (old) {
    old.remove();
  }
  const script = document.createElement("script");
  script.id = vmid;
  script.type = "application/ld+json";
  script.innerHTML = JSON.stringify(data);
  document.head.appendChild(script);
}

// ========== FAQPage 结构化数据（AI 问答引擎直接引用） ==========
export function getFaqJsonLd(faqs: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
}

// ========== HowTo 结构化数据（流程/步骤类页面） ==========
export function getHowToJsonLd(options: {
  name: string;
  description?: string;
  steps: Array<{ name: string; text: string; url?: string }>;
  totalTime?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: options.name,
    description: options.description || undefined,
    totalTime: options.totalTime || undefined,
    step: options.steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name,
      text: step.text,
      url: step.url || undefined
    }))
  };
}

// ========== AggregateRating 客户评分结构化数据 ==========
export function getAggregateRatingJsonLd(options: {
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  worstRating?: number;
  itemReviewed?: { name: string; type?: string };
}) {
  return {
    "@context": "https://schema.org",
    "@type": "AggregateRating",
    ratingValue: options.ratingValue,
    reviewCount: options.reviewCount,
    bestRating: options.bestRating || 5,
    worstRating: options.worstRating || 1,
    itemReviewed: options.itemReviewed
      ? {
          "@type": options.itemReviewed.type || "Organization",
          name: options.itemReviewed.name
        }
      : undefined
  };
}

// ========== ItemList 结构化数据（列表页） ==========
export function getItemListJsonLd(items: Array<{ name: string; url: string; image?: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      url: item.url.startsWith("http") ? item.url : `${SITE_CONFIG.url}${item.url}`,
      image: item.image || undefined
    }))
  };
}

// ========== AboutPage 结构化数据（关于页） ==========
export function getAboutPageJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: `${SITE_CONFIG.name} - 关于我们`,
    description: SITE_CONFIG.description,
    url: `${SITE_CONFIG.url}/about`,
    mainEntity: {
      "@type": "Organization",
      name: SITE_CONFIG.name,
      legalName: SITE_CONFIG.legalName,
      foundingDate: SITE_CONFIG.foundingDate,
      description: SITE_CONFIG.description
    }
  };
}

// ========== CollectionPage 结构化数据（资讯/案例/课程/师资列表） ==========
export function getCollectionPageJsonLd(options: {
  name: string;
  description: string;
  path: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: options.name,
    description: options.description,
    url: `${SITE_CONFIG.url}${options.path}`,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url
    }
  };
}

// ========== 生成页面 metaInfo ==========
export interface PageSeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  ogImage?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
  breadcrumb?: Array<{ name: string; url?: string }>;
}

export function generateMetaInfo(options: PageSeoOptions) {
  const fullTitle = options.title;
  const description = options.description;
  const keywords = options.keywords || SITE_CONFIG.keywords;
  const canonical = `${SITE_CONFIG.url}${options.path}`;
  const ogImage = options.ogImage || SITE_CONFIG.ogImage;

  // 收集所有 JSON-LD
  const jsonLdList: Record<string, any>[] = [];
  if (options.breadcrumb && options.breadcrumb.length > 0) {
    jsonLdList.push(getBreadcrumbJsonLd(options.breadcrumb));
  }
  if (options.jsonLd) {
    if (Array.isArray(options.jsonLd)) {
      jsonLdList.push(...options.jsonLd);
    } else {
      jsonLdList.push(options.jsonLd);
    }
  }

  const meta: any[] = [
    { vmid: "description", name: "description", content: description },
    { vmid: "keywords", name: "keywords", content: keywords },
    // Open Graph
    { vmid: "og:title", property: "og:title", content: fullTitle },
    { vmid: "og:description", property: "og:description", content: description },
    { vmid: "og:type", property: "og:type", content: "website" },
    { vmid: "og:url", property: "og:url", content: canonical },
    { vmid: "og:image", property: "og:image", content: ogImage },
    { vmid: "og:site_name", property: "og:site_name", content: SITE_CONFIG.name },
    { vmid: "og:locale", property: "og:locale", content: "zh_CN" },
    // Twitter Card
    { vmid: "twitter:card", name: "twitter:card", content: "summary_large_image" },
    { vmid: "twitter:title", name: "twitter:title", content: fullTitle },
    { vmid: "twitter:description", name: "twitter:description", content: description },
    { vmid: "twitter:image", name: "twitter:image", content: ogImage }
  ];

  const script: any[] = jsonLdList.map((jsonLd, index) => ({
    vmid: `jsonld-${index}`,
    type: "application/ld+json",
    innerHTML: JSON.stringify(jsonLd)
  }));

  return {
    title: fullTitle,
    meta,
    link: [{ vmid: "canonical", rel: "canonical", href: canonical }],
    script,
    __dangerouslyDisableSanitizersByTagID: script.reduce((acc: Record<string, string[]>, _: any, index: number) => {
      acc[`jsonld-${index}`] = ["innerHTML"];
      return acc;
    }, {})
  };
}
